import clsx from 'clsx'
import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import s from './styles.module.scss'

import { BillingColumns } from '../BillingColumns'
import { DescriptionMode } from '../DescriptionMode'
import LinesTotal from '../details-total/lines-total/LinesTotal'
import {
  ComputedPrice,
  DetailsVatTotalPerVatRatesFragment,
} from '../../../../../../lib/graphql'
import { divideByFactor } from '../../../../../../utils/prices'
import { useCurrencyAreaContext } from '../../../../../contexts'

interface IDetailsTotalProps {
  label?: string
  total: {
    totalPerVatRates: DetailsVatTotalPerVatRatesFragment[]
    price: ComputedPrice
  }
  className?: string
  descriptionMode?: DescriptionMode
  columns: BillingColumns[]
  showAdjustments?: boolean
  noColor?: boolean
}

const DetailsVat = ({
  total,
  className,
  descriptionMode,
  columns,
  showAdjustments = false,
}: IDetailsTotalProps) => {
  const intl = useIntl()
  const { vatRates } = useCurrencyAreaContext()

  const clientPricingItemVatRates = total.totalPerVatRates.reduce(
    (acc: number[], totalPerVatRate) => {
      const vatRate = totalPerVatRate.priceWithDiscount?.vatRate
      if (vatRate !== undefined) {
        acc.push(vatRate as number)
      }
      return acc
    },
    [],
  )

  // Permet de combiner les vatRates du lieu + celles des clients pricing items pour être sûr de tout afficher
  const computedVatRates = [
    ...new Set([...(vatRates ?? []), ...clientPricingItemVatRates]),
  ].sort((a, b) => a - b)

  return (
    <div className={clsx(s.blockContainer, className)}>
      {computedVatRates.map((vatRate) => {
        const priceWithDiscountForVatRate = total.totalPerVatRates.find(
          (total) => total.priceWithDiscount?.vatRate === vatRate,
        )?.priceWithDiscount

        return (
          <LinesTotal
            key={vatRate}
            label={intl.formatMessage(
              {
                id: 'ui.DetailsVat.brutProvisionsVatRate',
                defaultMessage: 'Brut Prestations à {vatRate}% de TVA',
              },
              { vatRate: divideByFactor(vatRate) },
            )}
            price={priceWithDiscountForVatRate}
            descriptionMode={descriptionMode}
            columns={columns}
            showAdjustments={showAdjustments}
          />
        )
      })}

      <LinesTotal
        label={
          <span>
            <FormattedMessage
              id='ui.DetailsVat.totalBrut'
              defaultMessage='Total Brut de vos prestations'
            />
            &nbsp;
            <span className={s.regular}>
              <FormattedMessage
                id='ui.DetailsVat.amountDisplayedToClient'
                defaultMessage='(montant affiché au client)'
              />
            </span>
          </span>
        }
        price={total.price}
        descriptionMode={descriptionMode}
        columns={columns}
        showAdjustments={showAdjustments}
        containerClassName={clsx(s.bold)}
        className={clsx(s.lineTotal)}
      />
    </div>
  )
}

export default DetailsVat
